import {ChangeDetectionStrategy, Component, input, OnDestroy, output, signal} from '@angular/core';
import {SessionViewComponent} from '../session-view.component';
import {Session} from '../../ts/session';
import {SessionState} from '../../ts/enum/session-state';
import {KeyCodes} from '../../ts/enum/key-codes';
import {buildKeyComboPlan, buildTextPlan, executePlan, KeySink, PlanExecution, SendStep} from './virtual-keyboard-sender';

interface SpecialKeyButton {
    label: string;
    icon?: string;
    keyCodes: number[];
}

@Component({
    selector: 'app-virtual-keyboard',
    templateUrl: './virtual-keyboard.component.html',
    styleUrls: ['./virtual-keyboard.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualKeyboardComponent implements OnDestroy {
    sessionView = input.required<SessionViewComponent>();
    closed = output<void>();

    sending = signal(false);

    readonly specialKeys: SpecialKeyButton[] = [
        {label: 'Enter', icon: 'keyboard_return', keyCodes: [KeyCodes.Return]},
        {label: 'Tab', icon: 'keyboard_tab', keyCodes: [KeyCodes.Tab]},
        {label: 'Backspace', icon: 'backspace', keyCodes: [KeyCodes.Backspace]},
        {label: 'Esc', keyCodes: [KeyCodes.Esc]},
        {label: '←', keyCodes: [KeyCodes.Left]},
        {label: '↑', keyCodes: [KeyCodes.Up]},
        {label: '↓', keyCodes: [KeyCodes.Down]},
        {label: '→', keyCodes: [KeyCodes.Right]},
        {label: 'Ctrl+Alt+Del', keyCodes: [KeyCodes.Ctrl, KeyCodes.Alt, KeyCodes.Delete]},
    ];

    private execution: PlanExecution | null = null;
    private planQueue: SendStep[][] = [];

    canSend(): boolean {
        const session = this.sessionView().session;
        return !!session && session.state === SessionState.Connected;
    }

    sendText(inputEl: HTMLInputElement, withEnter = false): void {
        const text = inputEl.value;
        if (!text || !this.canSend()) {
            return;
        }
        inputEl.value = '';
        inputEl.focus();
        this.enqueuePlan(buildTextPlan(withEnter ? text + '\n' : text));
    }

    sendSpecialKey(button: SpecialKeyButton): void {
        if (!this.canSend()) {
            return;
        }
        this.enqueuePlan(buildKeyComboPlan(button.keyCodes));
    }

    close(): void {
        this.closed.emit();
    }

    ngOnDestroy(): void {
        this.planQueue.length = 0;
        this.execution?.cancel();
        this.execution = null;
    }

    private enqueuePlan(plan: SendStep[]): void {
        if (this.execution) {
            this.planQueue.push(plan);
            return;
        }
        this.startPlan(plan);
    }

    private startPlan(plan: SendStep[]): void {
        const sink = this.makeSink(this.sessionView().session);
        this.sending.set(true);
        const execution = executePlan(plan, sink);
        this.execution = execution;
        execution.done.then(() => {
            if (this.execution !== execution) {
                return;
            }
            this.execution = null;
            const next = this.planQueue.shift();
            if (next) {
                this.startPlan(next);
            } else {
                this.sending.set(false);
            }
        });
    }

    private makeSink(session: Session): KeySink {
        return {
            keyDown: keyCode => session.keyDown(keyCode),
            keyUp: keyCode => session.keyUp(keyCode),
            keyUnicodeDown: codePoint => session.keyUnicodeDown(codePoint),
            keyUnicodeUp: codePoint => session.keyUnicodeUp(codePoint),
        };
    }
}
